import { useCallback } from 'react';
import type { MentionClickData } from '@cotera/client/v0/components/ai-elements/readonly-editor-state';
import { ResourceType } from '@cotera/api';
import {
  parseResourceMention,
  parseWorkflowToolMention,
} from '@cotera/client/v0/chat/artifacts';
import { useResourceNavigator } from '../v1/resources/navigation';

/**
 * Resolve a clicked workspace-resource `@` mention to the best available surface.
 *
 * Surface selection lives in {@link useResourceNavigator}: an editor page's side
 * panel, else a sibling artifact tab in the chat panel, else the resource's full
 * page. This is what makes clicking an `@document` inside an agent show the
 * document as another artifact instead of navigating away.
 *
 * Also handles workflow-tool mentions (`coco/workflow-<id>`) the same way, so
 * clicking a workflow tool in an agent/document editor opens the workflow in a
 * panel rather than falling through to the generic tool modal.
 *
 * Returns `true` when the click was handled as a resource mention (so the caller
 * can skip tool-mention handling), `false` otherwise.
 */
export function useOpenResourceMention(): (data: MentionClickData) => boolean {
  const navigator = useResourceNavigator();

  return useCallback(
    (data: MentionClickData) => {
      const workflowId = parseWorkflowToolMention(data);
      if (workflowId !== null) {
        navigator.open({
          resourceType: ResourceType.Workflow,
          resourceId: workflowId,
        });
        return true;
      }

      const resource = parseResourceMention(data);
      if (resource === null) {
        return false;
      }
      navigator.open({
        resourceType: resource.resourceType,
        resourceId: resource.resourceId,
      });
      return true;
    },
    [navigator]
  );
}
